import React, { useEffect, useMemo, useState } from "react";
import {
  getDashboardSummary,
  getMonthlyCollection,
  getRecentTransactions,
  getChannelSummary,
  getContributionTypeSummary,
  getTopJumuia,
} from "../../services/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import "./reports.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

export default function ReportsDashboard() {
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
  });

  const [summary, setSummary] = useState({});
  const [monthly, setMonthly] = useState([]);
  const [recent, setRecent] = useState([]);
  const [channelSummary, setChannelSummary] = useState([]);
  const [typeSummary, setTypeSummary] = useState([]);
  const [topJumuia, setTopJumuia] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const params = {
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      };

      const [
        summaryRes,
        monthlyRes,
        recentRes,
        channelRes,
        typeRes,
        topJumuiaRes,
      ] = await Promise.all([
        getDashboardSummary(params),
        getMonthlyCollection(params),
        getRecentTransactions(),
        getChannelSummary(params),
        getContributionTypeSummary(params),
        getTopJumuia(params),
      ]);

      setSummary(summaryRes.data || {});
      setMonthly(monthlyRes.data || []);
      setRecent(recentRes.data || []);
      setChannelSummary(channelRes.data || []);
      setTypeSummary(typeRes.data || []);
      setTopJumuia(topJumuiaRes.data || []);
    } catch (err) {
      console.error("Failed to load dashboard", err);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const monthlyChartData = useMemo(
    () => ({
      labels: monthly.map((m) => m.month_label),
      datasets: [
        {
          label: "Collection Amount",
          data: monthly.map((m) => Number(m.total_amount || 0)),
          backgroundColor: "rgba(59, 130, 246, 0.75)",
          borderRadius: 6,
        },
      ],
    }),
    [monthly]
  );

  const channelChartData = useMemo(
    () => ({
      labels: channelSummary.map((c) => c.channel),
      datasets: [
        {
          label: "Amount",
          data: channelSummary.map((c) => Number(c.total_amount || 0)),
          backgroundColor: [
            "rgba(59, 130, 246, 0.78)",
            "rgba(16, 185, 129, 0.78)",
            "rgba(245, 158, 11, 0.78)",
          ],
          borderWidth: 1,
        },
      ],
    }),
    [channelSummary]
  );

  return (
    <div className="page-container">
      <div className="reports-header">
        <div>
          <h1>Reports Analytics Dashboard</h1>
          <p className="subtitle">
            Collections and member analytics for your Parokia
          </p>
        </div>
      </div>

      <div className="card report-filter-card">
        <div className="report-filter-grid report-filter-grid-small">
          <div>
            <label className="field-label">Date From</label>
            <input
              type="date"
              className="input"
              value={filters.dateFrom}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="field-label">Date To</label>
            <input
              type="date"
              className="input"
              value={filters.dateTo}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, dateTo: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="report-filter-actions">
          <button className="btn primary" onClick={loadDashboard}>
            Apply Filters
          </button>
        </div>
      </div>

      <div className="report-stats-grid">
        <div className="card report-stat-card">
          <div className="report-stat-label">Total Collection</div>
          <div className="report-stat-value">TSH {formatMoney(summary.totalCollection)}</div>
        </div>

        <div className="card report-stat-card">
          <div className="report-stat-label">Transactions</div>
          <div className="report-stat-value">{summary.totalTransactions || 0}</div>
        </div>

        <div className="card report-stat-card">
          <div className="report-stat-label">Posted</div>
          <div className="report-stat-value">{summary.postedCount || 0}</div>
        </div>

        <div className="card report-stat-card">
          <div className="report-stat-label">Members</div>
          <div className="report-stat-value">{summary.totalMembers || 0}</div>
        </div>
      </div>

      <div className="report-chart-grid">
        <div className="card">
          <h3>Monthly Collection Trend</h3>
          {loading ? <div className="report-loading">Loading...</div> : <Bar data={monthlyChartData} />}
        </div>

        <div className="card">
          <h3>Channel Summary</h3>
          {loading ? <div className="report-loading">Loading...</div> : <Doughnut data={channelChartData} />}
        </div>
      </div>

      <div className="report-chart-grid">
        <div className="card">
          <h3>Contribution Type Summary</h3>
          <div className="report-mini-list">
            {typeSummary.map((row, i) => (
              <div className="report-mini-row" key={i}>
                <span>{row.contribution_type}</span>
                <strong>TSH {formatMoney(row.total_amount)}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3>Top Jumuiya by Contribution</h3>
          <div className="report-mini-list">
            {topJumuia.map((row, i) => (
              <div className="report-mini-row" key={i}>
                <span>{row.jumuia_name}</span>
                <strong>TSH {formatMoney(row.total_amount)}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Recent Transactions</h3>
        <div className="report-table-wrap">
          <table className="table report-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Reference</th>
                <th>Member</th>
                <th>Contribution</th>
                <th>Amount</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty">No records found</td>
                </tr>
              ) : (
                recent.map((row, index) => (
                  <tr key={row.transaction_id}>
                    <td>{index + 1}</td>
                    <td>{row.reference_no || "-"}</td>
                    <td>{row.full_name || "-"}</td>
                    <td>{row.contribution_type || "-"}</td>
                    <td>TSH {formatMoney(row.amount)}</td>
                    <td>{row.channel || "-"}</td>
                    <td>{row.status || "-"}</td>
                    <td>
                      {row.transaction_date
                        ? new Date(row.transaction_date).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}